// 事件收件匣與全文檢索（plan/vms-web-v0.md §5.7.1 EVT-01/02、§5.7.2 SRCH-01/02）：
//   - 來源 GET /v1/vms/search/events（伺服器依授權展開範圍；facet 只算範圍內），不同 plane 的家戶各查一次再合併
//   - 搜尋框：自由文字 + 欄位語法（class: device: tag: after: before: has:clip、"片語"、-排除），輸入時提示設備 / 類別 / 標籤
//   - 分級 chip 來自 facet；另有設備 / 標籤 facet 與每小時分布；依時間或相關度排序，cursor 分頁
//   - 即時新增（EVT-01）：事件串流（/v1/vms/stream）進來的直接插在最上面（依時間排序、沒有文字查詢時），索引跟上後以索引那筆為準；
//     串流斷了才靠 60 秒輪詢補
//   - 每筆可開事件詳細（EVT-04）
//   - 頁內分頁「警報（未處理 N）｜全部事件｜已存搜尋」（SPEC EVT-02 頁內分頁；第二輪 R2-P1-3）：有未處理警報時預設「警報」
//   - 設備 facet：沒登記的通道合併成「其他通道（N）▸」、同名的「不在清單裡的設備」帶末碼；tag 照字串表翻；聲音類歸「偵測」、聲音圖示
import { DateTimeInput } from '../../components/DateTimeInput';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { lensOf, type KinioEvent } from '@kinio/web-sdk';
import { LensChip } from '../../components/LensChip';
import { useScope } from '../../lib/scope';
import { useLiveEvents } from '../../lib/liveEvents';
import { orgApi, searchApi, type Facet, type SearchDoc, type SearchResponse } from '../../lib/planeApi';
import { extractSiteTokens, siteScope } from '../../lib/siteSearch';
import { expandEventTerms } from '../../lib/eventTerms';
import { isUnseen, useEventSeen } from '../../lib/eventSeen';
import { SavedSearches } from './SavedSearches';
import { EventThumb } from '../../components/EventThumb';
import { useDevices } from '../../lib/useDevices';
import { CHILD_SEP, can, eventDeviceLabel, fullName, parseChildAid, type VmsDevice } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { nearStamp, sinceStamp, stamp } from '../../lib/timefmt';
import { compareTidy, handling, openAgeText, recoveredAt, TIDY_RANK, tidyRank } from '../../lib/alarmTidy';
import { useNow } from '../../lib/useNow';
import { useTzResolver } from '../../lib/useTz';
import { AlarmActions } from '../../components/AlarmActions';
import { compareAlarm, groupAlarms, type AlarmGroup } from '../../lib/alarmGroup';
import { MoreMenu } from '../../components/MoreMenu';
import { HourHistogram } from './HourHistogram';
import { meTreeApi, type MeTreeItem } from '../../lib/orgTreeApi';
import { useMeId, useViewsAndGroups } from '../orgtree/hooks';
import { alertClassOf, eventName, isFeedOnly } from '../../lib/eventName';
import { playbackHref } from '../../lib/links';
import { ignoreHotkey } from '../../lib/hotkeys';
import { aggKey, useAlarmStates } from '../../lib/alarmStates';
import { isUnhandled } from '../../lib/alarms';
import { episodeState, mergeEpisodeRows, phaseOf, type Episode } from '../../lib/eventEpisodes';
import { durationText } from './EventDetailPage';
import { AlarmWorkbench, type WbItem } from './AlarmWorkbench';
import { workbenchMode } from '../../lib/alarmWorkbench';
import { useViewportWidth } from '../../lib/useViewport';
import { hasDutyMode } from '../overview/OverviewPage';

/**
 * VMS 的事件分級（EVT-02，仿 Frigate Review）：警報 / 偵測 / 動態 / 感測器 / 系統 / 其他。
 * 伺服器投影時算好（search.event_docs.level，警報要看採集當下的布防設定）；串流插進來、索引還沒跟上的列用 classify 依 class 推
 * （跟 platform search.LevelOf 同一張表；沒有布防資訊，所以串流列只有 alarm / tamper 會是警報）。
 */
export type VmsEventClass = 'alarm' | 'detection' | 'motion' | 'sensor' | 'system' | 'other';

const LEVEL_OF_CLASS: Record<string, VmsEventClass> = {
  alarm: 'alarm',
  tamper: 'alarm',
  person: 'detection',
  human: 'detection',
  vehicle: 'detection',
  pet: 'detection',
  package: 'detection',
  ai_label: 'detection',
  motion: 'motion',
  // 聲音類（狗叫、玻璃破裂、哭聲…）在畫面上歸「偵測」（SPEC EVT-02 facet、標籤與分類；第二輪 B2-14）。
  // 平台 search.LevelOf 還把 sound 歸 motion（platform 待辦），levelOfDoc 先在畫面上改過來
  sound: 'detection',
  contact: 'sensor',
  occupancy: 'sensor',
  doorbell: 'sensor',
  call: 'sensor',
  connection: 'system',
  storage: 'system',
  recording: 'system',
  power: 'system',
  ota: 'system',
  credential: 'system',
  system: 'system',
  security_mode: 'system',
};

export function isLevel(v: string | undefined): v is VmsEventClass {
  return !!v && (CLASSES as string[]).includes(v);
}

/** eventClass：事件 feed 的 class（gateway 帶的 event_class），有就優先；沒有就看事件名稱。 */
export function classify(type: string, eventClass?: string): VmsEventClass {
  // 分級照警報判斷用的 class（alert_class；區域入侵 / 越線 class alarm、alert_class motion → 沒有布防資訊時是「動態」，同 platform search.LevelOf）
  const c = (alertClassOf(type, eventClass) ?? '').toLowerCase();
  if (LEVEL_OF_CLASS[c]) return LEVEL_OF_CLASS[c];
  const t = type.trim().toLowerCase();
  if (t.startsWith('boolean_state.') || t.startsWith('occupancy_sensing.')) return 'sensor';
  if (t === 'human' || t === 'person' || t.endsWith('.person') || t.endsWith('.human')) return 'detection';
  if (t === 'vehicle' || t === 'car' || t.endsWith('.vehicle')) return 'detection';
  if (t === 'motion' || t === 'pir' || t.endsWith('.motion')) return 'motion'; // pir = 本機警報舊名，上雲是 kinio_motion.motion{source: pir}
  if (['smoke', 'leak', 'water', 'tamper'].includes(t)) return 'alarm';
  if (['door', 'contact', 'button', 'doorbell'].includes(t) || t.startsWith('sensor.')) return 'sensor';
  if (t.startsWith('thing.') || t.startsWith('ota.') || t === 'online' || t === 'offline' || t.startsWith('storage') || t.startsWith('video_loss'))
    return 'system';
  return 'other';
}

/** 一筆搜尋結果的分級：伺服器給的 level 優先（有布防資訊）；level 空 = 依 class 推。 */
export function levelOfDoc(doc: { level?: string; class: string; key: string }): VmsEventClass {
  if (doc.class === 'sound' && doc.level === 'motion') return 'detection'; // 平台改之前（B2-14）
  return isLevel(doc.level) ? doc.level : classify(doc.key, doc.class);
}

/** 列上的圖示：聲音類用聲音圖示（不用跑步的人，B2-14）。 */
export function eventIcon(cls: VmsEventClass, eventClass?: string): string {
  if (eventClass === 'sound') return '🔊';
  return CLASS_ICON[cls];
}

/** 事件 tag 的顯示：照字串表翻（#person →「人」），表裡沒有的照原樣（第二輪 R2-P1-3）。 */
export function tagLabel(t: (k: string, o?: Record<string, unknown>) => string, tag: string): string {
  return t(`events.tag.${tag.toLowerCase()}`, { defaultValue: tag });
}

export type EventsTab = 'alarms' | 'all' | 'saved';
export const EVENTS_TABS: EventsTab[] = ['alarms', 'all', 'saved'];

/** 沒指定分頁時的預設：有未處理的警報 →「警報」，否則「全部事件」；還不知道（null）→ 先「全部事件」。 */
export function defaultEventsTab(unhandled: number | null): EventsTab {
  return unhandled ? 'alarms' : 'all';
}

/**
 * 設備 facet 的顯示（第二輪 R2-P1-3、B2-13 附近）：沒登記的通道（parent 認得、通道本身不在清單）合併成一組；
 * 不認得的設備有好幾個時各帶 ID 末 4 碼區分（只有一個就不帶）。
 */
export function groupDeviceFacet<F extends { value: string; count: number }>(
  facet: F[],
  kind: (ref: string) => 'known' | 'channel' | 'unknown',
): { main: (F & { tail?: string })[]; channels: (F & { tail?: string })[]; unknown: number } {
  // 沒登記的通道與不在清單裡的設備都收進「其他（N）▸」，不各佔一顆膠囊（第四輪 B4-07）
  const unknownN = facet.filter((f) => kind(f.value) === 'unknown').length;
  const channels = facet
    .filter((f) => kind(f.value) !== 'known')
    .map((f) => (kind(f.value) === 'unknown' && unknownN > 1 ? { ...f, tail: f.value.replace(/[^0-9A-Za-z]/g, '').slice(-4) } : f));
  const main = facet.filter((f) => kind(f.value) === 'known');
  return { main, channels, unknown: unknownN };
}

const CLASS_ICON: Record<VmsEventClass, string> = { alarm: '🚨', detection: '👤', motion: '🏃', sensor: '🚪', system: '⚙️', other: '•' };
const CLASSES: VmsEventClass[] = ['alarm', 'detection', 'motion', 'sensor', 'system', 'other'];
const FIELDS = ['class:', 'level:', 'device:', 'site:', 'node:', 'view:', 'group:', 'tag:', 'after:', 'before:', 'has:clip'];

/** 選過的節點名稱存在這個瀏覽器，key 帶 user_id（共用電腦換帳號不會看到前一個人選過的名稱，app/vms#4） */
const nodeNamesKey = (userId: string) => `vms.search.nodeNames:${userId}`;

/** 搜尋提示：label 是給人看的（名稱 + 路徑），insert 是放進搜尋框的 token。 */
interface Suggestion {
  label: string;
  insert: string;
}

/**
 * 樹搜尋結果的節點 → `node:` 的值（SPEC #org-tree-impact 搜尋語法）：本組織 `<site_id>`，經委派 `<site_id>@<delegation_id>`。
 * key 形狀 `node:<org_id 或 dlg:<delegation_id>>:<site_id>`（#org-tree-api /v1/me/tree）。
 */
export function nodeRefOf(it: Pick<MeTreeItem, 'key' | 'id'>): string {
  const m = /^node:dlg:([^:]+):/.exec(it.key);
  return m ? `${it.id}@${m[1]}` : it.id;
}

/** 搜尋字串裡的 node: / view: / group: 值（逗號 = 或），給「篩選中」那一行換成名稱。 */
export function orgTreeTokens(q: string): { field: 'node' | 'view' | 'group'; value: string }[] {
  const out: { field: 'node' | 'view' | 'group'; value: string }[] = [];
  for (const w of q.split(/\s+/)) {
    const m = /^(node|view|group):(.+)$/i.exec(w); // 欄位名大小寫不敏感（同伺服器 search.Parse）
    if (!m) continue;
    for (const v of m[2].split(',')) if (v) out.push({ field: m[1].toLowerCase() as 'node' | 'view' | 'group', value: v });
  }
  return out;
}

/** 事件歸屬的設備：payload.ch（NVR / station 的通道，0 起算）→ 子設備 `<parent>:ch<n>`。 */
export function eventDeviceAid(ev: Pick<KinioEvent, 'device_ref' | 'payload'>): string {
  const ch = ev.payload?.ch;
  return typeof ch === 'number' && Number.isInteger(ch) && ch >= 0 && !ev.device_ref.includes(CHILD_SEP) ? `${ev.device_ref}${CHILD_SEP}${ch}` : ev.device_ref;
}

interface Row {
  doc: SearchDoc;
  /** 來自哪個 plane（已看狀態每個 plane 一份，EVT-03）；串流插進來的沒有 */
  api?: string;
  /** 從事件串流插進來、索引還沒跟上的 */
  live?: boolean;
  cls: VmsEventClass;
  device?: VmsDevice;
  homeName: string;
  ts: number;
  /** 同一次的開始＋結束合成一列（第八輪 R8-P1-1、B8-02）：這一列代表的那一次 */
  ep?: Episode;
  /** 警報分頁補的「還沒處理」列（openHeads）：未處理清單已經配好的那一次（結束那筆不在這一頁也知道持續多久；第十輪 B10-03） */
  presetEp?: Episode;
}

/** 依時間新到舊；同一秒照總覽的順序（設備鍵、key、event_id；第十一輪 B11-05） */
const byTime = (a: Row, b: Row) => compareAlarm({ ts: a.ts, ref: a.doc.device_ref, key: a.doc.key, id: a.doc.event_id }, { ts: b.ts, ref: b.doc.device_ref, key: b.doc.key, id: b.doc.event_id });

export function EventsPage() {
  const { t, i18n } = useTranslation();
  const { tree } = useDevices();
  const { visible, orgs, planes } = useScope();
  const meId = useMeId();
  const siteQs = useQueries({ queries: orgs.map((o) => ({ queryKey: ['sites', o.org_id], queryFn: () => orgApi.sites(o), staleTime: 60_000, retry: false })) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sites = useMemo(() => siteQs.flatMap((x) => x.data ?? []), [siteQs.map((x) => x.dataUpdatedAt).join()]);
  const tzOf = useTzResolver();
  const [enabledAll, setEnabled] = useState<Set<VmsEventClass>>(new Set(DEFAULT_LEVELS));
  const { unhandled, unhandledMore, hasUnhandled, infoOf, openList, openAgg, openReady, apiOf, listed } = useAlarmStates();
  // 「已開 N 分鐘」每分鐘更新（SPEC #vms-web-alarm-tidy）
  const now = useNow(60_000);
  // 時間範圍（P1-3 ②、B-16）：產生 on: / after: / before: 語法並帶 tz（日界用使用者時區，平台照這個算）；不寫進搜尋框
  // 頁首全域搜尋的「在事件中搜尋」帶 ?q=（SPEC #vms-web-220 全域搜尋）；時間範圍也放網址（?range= 或自訂的 ?from=&to=），重新整理與分享連結回到同一個查詢
  const [sp, setSp] = useSearchParams();
  const urlRange = sp.get('range') as TimeRange | null;
  const range: TimeRange = sp.get('from') || sp.get('to') ? 'custom' : urlRange && TIME_RANGES.includes(urlRange) ? urlRange : 'any';
  const [customOpen, setCustomOpen] = useState(false);
  const custom = useMemo(() => ({ from: sp.get('from') ?? '', to: sp.get('to') ?? '' }), [sp]);
  const shownRange: TimeRange = customOpen ? 'custom' : range;
  // 從「現在」的網址合併：react-router 6 的 setSp(fn) 給的是那次 render 的參數，延遲寫 q（400ms）會把期間切的分頁蓋回去（vms SRCH-03 不穩）
  const setUrl = (patch: Record<string, string>) =>
    setSp(
      () => {
        const n = new URLSearchParams(window.location.search);
        for (const [k, v] of Object.entries(patch)) {
          if (v) n.set(k, v);
          else n.delete(k);
        }
        return n;
      },
      { replace: true },
    );
  const setRange = (r: TimeRange) => {
    setCustomOpen(r === 'custom');
    if (r !== 'custom') setUrl({ range: r === 'any' ? '' : r, from: '', to: '' });
  };
  const setCustom = (c: { from: string; to: string }) => setUrl({ range: '', from: c.from, to: c.to });
  // 頁內分頁（網址 ?tab=）：沒指定時第一次知道未處理數就決定預設，之後不跟著跳
  const urlTab = sp.get('tab') as EventsTab | null;
  const [defTab, setDefTab] = useState<EventsTab | null>(null);
  // 值班模式（AUTH-08）帳號一律預設「警報」（工作台；沒有未處理的也是，SPEC #vms-web-alarm-workbench）
  const dutyMode = hasDutyMode(visible, orgs);
  useEffect(() => {
    if (defTab !== null) return;
    if (dutyMode) setDefTab('alarms');
    else if (hasUnhandled !== null) setDefTab(defaultEventsTab(hasUnhandled ? 1 : 0));
  }, [hasUnhandled, defTab, dutyMode]);
  const tab: EventsTab = urlTab && EVENTS_TABS.includes(urlTab) ? urlTab : (defTab ?? 'all');
  const setTab = (x: EventsTab) => setUrl({ tab: x });
  // 警報工作台（EVT-07）：警報分頁 ≥ 1280 左右分割、768–1279 清單＋抽屜；手機照舊整頁清單
  const vw = useViewportWidth();
  const wbMode = workbenchMode(vw);
  const wbOn = tab === 'alarms' && wbMode !== 'mobile';
  // 「警報」分頁 = 只看警報（分級標籤不顯示）；「全部事件」照分級標籤
  const enabled = useMemo(() => (tab === 'alarms' ? new Set<VmsEventClass>(['alarm']) : enabledAll), [tab, enabledAll]);
  const [chOpen, setChOpen] = useState(false);
  const rangeQ = useMemo(() => rangeTokens(range, new Date(), custom), [range, custom]);
  const urlQ = sp.get('q') ?? '';
  const [input, setInput] = useState(urlQ);
  const [q, setQ] = useState(urlQ.trim());
  const inputRef = useRef(input);
  inputRef.current = input;
  // 最後一次由輸入框寫進網址的 q：網址晚一步變成這個值時（寫完之後使用者又點了提示），不要拿舊字蓋回輸入框（app/vms#29）
  const wroteQ = useRef<string | null>(null);
  useEffect(() => {
    // 網址的 q 是從輸入框寫回去的（trim 過）：一樣就不蓋輸入框（打到一半的空白不被吃掉）
    if (urlQ === wroteQ.current) return;
    if (urlQ && urlQ !== inputRef.current.trim()) setInput(urlQ);
  }, [urlQ]);
  const [sort, setSort] = useState<'time' | 'relevance'>('time');
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  useEffect(() => {
    const id = setTimeout(() => {
      setQ(input.trim());
      // 搜尋條件寫進網址（第八輪 B8-10：從詳細按上一頁回來，搜尋框清空、回到全部事件）
      if ((sp.get('q') ?? '') !== input.trim()) {
        wroteQ.current = input.trim();
        setUrl({ q: input.trim() });
      }
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // SRCH-01 site:：換成那些場域（含子場域）底下的家戶，當搜尋範圍（伺服器索引沒有場域欄）
  const siteTok = useMemo(() => extractSiteTokens(q), [q]);
  const siteSc = useMemo(() => siteScope(siteTok.values, sites, visible), [siteTok, sites, visible]);
  const byApi = useMemo(() => {
    const m = new Map<string, string[]>();
    const only = siteSc.homes ? new Set(siteSc.homes) : null;
    for (const h of visible) if (!only || only.has(h.home_id)) m.set(h.endpoints.api, [...(m.get(h.endpoints.api) ?? []), h.home_id]);
    return m;
  }, [visible, siteSc]);
  const allOn = enabled.size === CLASSES.length;
  const levelQ = allOn ? '' : 'level:' + CLASSES.filter((c) => enabled.has(c)).join(',');
  // 「拆除」「影像訊號中斷」這類事件種類的說法換成 class:（第七輪 B7-01；伺服器 body 沒有這些字）
  const evTerms = useMemo(() => expandEventTerms(siteTok.rest), [siteTok.rest]);
  const fullQ = [evTerms.q, rangeQ, enabled.size ? levelQ : 'class:__none__'].filter(Boolean).join(' ');

  const query = useInfiniteQuery({
    queryKey: ['search', fullQ, sort, [...byApi.entries()].map(([a, h]) => a + ':' + h.join('+')).join('|')],
    enabled: byApi.size > 0,
    refetchInterval: 60_000,
    initialPageParam: {} as Record<string, string>,
    queryFn: async ({ pageParam }): Promise<{ parts: Record<string, SearchResponse> }> => {
      const parts: Record<string, SearchResponse> = {};
      await Promise.all(
        [...byApi.entries()].map(async ([api, homes]) => {
          if (pageParam[api] === '') return; // 這個 plane 沒有下一頁了
          const r = await searchApi.events(api, { q: fullQ, homes, sort, cursor: pageParam[api], tz });
          if (r) parts[api] = r;
        }),
      );
      return { parts };
    },
    getNextPageParam: (last) => {
      const next: Record<string, string> = {};
      let any = false;
      for (const api of byApi.keys()) {
        const c = last.parts[api]?.next_cursor ?? '';
        next[api] = c;
        any = any || c !== '';
      }
      return any ? next : undefined;
    },
  });

  const first = query.data?.pages[0]?.parts ?? {};
  const merge = (pick: (r: SearchResponse) => Facet[]) => {
    const m = new Map<string, number>();
    for (const r of Object.values(first)) for (const f of pick(r)) m.set(f.value, (m.get(f.value) ?? 0) + f.count);
    return [...m.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
  };
  const levelFacet = merge((r) => r.facets.level);
  const deviceFacet = merge((r) => r.facets.device).slice(0, 12);
  const tagFacet = merge((r) => r.facets.tag).slice(0, 12);
  const hourFacet = merge((r) => r.facets.hour)
    .sort((a, b) => a.value.localeCompare(b.value))
    .slice(-48);
  const total = Object.values(first).reduce((n, r) => n + r.total, 0);
  const capped = Object.values(first).some((r) => r.capped);
  const warnings = Object.values(first).flatMap((r) => r.warnings ?? []);
  const engine = Object.values(first)[0]?.engine;
  const took = Math.max(0, ...Object.values(first).map((r) => r.took_ms));

  const byAid = useMemo(() => {
    const m = new Map<string, VmsDevice>();
    for (const d of tree) {
      m.set(`${d.homeId}/${d.aid}`, d);
      for (const c of d.children) m.set(`${c.homeId}/${c.aid}`, c);
    }
    return m;
  }, [tree]);
  const homeName = (id: string) => visible.find((h) => h.home_id === id)?.display_name || id;
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const page of query.data?.pages ?? [])
      for (const [api, r] of Object.entries(page.parts))
        for (const doc of r.data) {
          const ev = { device_ref: doc.device_ref, payload: doc.payload } as Pick<KinioEvent, 'device_ref' | 'payload'>;
          out.push({
            doc,
            api,
            cls: levelOfDoc(doc),
            device: byAid.get(`${doc.home_id}/${eventDeviceAid(ev)}`) ?? byAid.get(`${doc.home_id}/${doc.device_ref}`),
            homeName: homeName(doc.home_id),
            ts: Date.parse(doc.occurred_at),
          });
        }
    // 同一秒的列跟總覽同一個順序（第十一輪 B11-05）
    return sort === 'time' ? out.sort(byTime) : out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, byAid, sort]);
  // 串流插入：只在依時間排序、沒有文字查詢時（文字 / 欄位條件要伺服器算）
  const { recent: streamed } = useLiveEvents();
  const liveRows: Row[] = useMemo(() => {
    if (sort !== 'time' || q) return [];
    const visibleIds = new Set(visible.map((h) => h.home_id));
    const newest = rows.length ? rows[0].ts : 0;
    const dup = (id: string, ref: string, key: string, ts: number) =>
      rows.some((r) => r.doc.event_id === id || (r.doc.device_ref === ref && r.doc.key === key && Math.abs(r.ts - ts) < 2000));
    const out: Row[] = [];
    for (const e of streamed) {
      const ts = Date.parse(e.event.ts);
      const cls = classify(e.event.type, e.event.event_class);
      // 設備用串流的設備鍵（通道 = <主機>:chN，跟索引列同一個；第九輪 B9-02：只用主機配不到清單上的「進行中」）
      const ref = e.deviceRef || e.event.device_ref;
      // feed_only 的狀態紀錄不進索引，串流也不插（不然片段上傳每 15 秒插一列，重新整理又消失；B12-07 收尾）
      if (isFeedOnly(e.event.type)) continue;
      if (range === 'yesterday' || range === 'custom' || !visibleIds.has(e.homeId) || !enabled.has(cls) || !(ts >= newest - 60_000) || dup(e.event.id, ref, e.event.type, ts)) continue;
      const doc: SearchDoc = {
        event_id: e.event.id,
        home_id: e.homeId,
        device_ref: ref,
        class: e.event.event_class ?? '',
        key: e.event.type,
        occurred_at: e.event.ts,
        has_clip: false,
        ai_tags: [],
        // 多鏡頭：即時推進來的事件把 lens 放進 payload（跟搜尋列一樣，平台 searchproj docPayload）
        payload: lensOf(e.event) ? { ...e.event.payload, lens: lensOf(e.event) } : e.event.payload,
        phase: typeof e.event.phase === 'string' ? e.event.phase : undefined,
        binding_id: typeof e.event.binding_id === 'string' && e.event.binding_id ? e.event.binding_id : undefined,
      };
      const ev = { device_ref: e.event.device_ref, payload: e.event.payload } as Pick<KinioEvent, 'device_ref' | 'payload'>;
      out.push({
        doc,
        cls,
        live: true,
        device: byAid.get(`${e.homeId}/${e.deviceRef}`) ?? byAid.get(`${e.homeId}/${eventDeviceAid(ev)}`),
        homeName: homeName(e.homeId),
        ts,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamed, rows, sort, q, enabled, byAid, visible, range]);
  // 警報分頁（依時間、沒有查詢條件）：還沒處理的每一台＋每一種至少一列——第一頁沒載到的（例：CH2 247 則都比第一頁舊）補一列在它最新那則的時間
  // （第六輪 B6-03：原本要按〔載入更多〕才看得到 CH2）
  const openHeads: Row[] = useMemo(() => {
    if (tab !== 'alarms' || sort !== 'time' || q || range !== 'any') return [];
    // 第一頁與完整的未處理清單都到了才補（第十輪 B10-03：清單還在載入時先畫補的列、寫「沒有收到結束紀錄」，幾秒後換成「持續 36 秒」又重排）
    if (!query.data || !openReady) return [];
    const have = new Set([...liveRows, ...rows].map((r) => aggKey(r.doc.home_id, r.doc.device_ref, r.doc.key)));
    const out: Row[] = [];
    for (const a of openList) {
      const k = aggKey(a.home_id, a.device_ref, a.key);
      if (have.has(k)) continue;
      have.add(k);
      const ev = { device_ref: a.device_ref, payload: {} } as Pick<KinioEvent, 'device_ref' | 'payload'>;
      out.push({
        doc: { event_id: a.event_id, home_id: a.home_id, device_ref: a.device_ref, class: a.class, key: a.key, occurred_at: a.occurred_at, has_clip: a.has_clip, ai_tags: [], payload: {}, phase: a.phase, binding_id: a.binding_id },
        presetEp: a.ep,
        api: apiOf(a.home_id),
        cls: 'alarm',
        device: byAid.get(`${a.home_id}/${eventDeviceAid(ev)}`) ?? byAid.get(`${a.home_id}/${a.device_ref}`),
        homeName: homeName(a.home_id),
        ts: Date.parse(a.occurred_at) || 0,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sort, q, range, openList, openReady, query.data, liveRows, rows, byAid]);
  const sortedRows = liveRows.length || openHeads.length ? [...liveRows, ...rows, ...openHeads].sort(byTime) : rows;
  // 同一次的開始＋結束合成一列（第八輪 R8-P1-1、B8-02；B7-17 資料確認：同一個 device_ref＋binding_id，phase started → ended 交替）
  const hasMore = !!query.hasNextPage;
  const shownRows = useMemo(() => {
    const merged = mergeEpisodeRows(sortedRows, (r) => {
      const ph = phaseOf(r.doc);
      if (!ph) return null;
      const ev = { device_ref: r.doc.device_ref, payload: r.doc.payload } as Pick<KinioEvent, 'device_ref' | 'payload'>;
      return { id: r.doc.event_id, ts: r.ts, phase: ph, ref: `${r.doc.home_id}/${eventDeviceAid(ev)}`, binding: r.doc.binding_id || undefined, key: r.doc.key };
    }).map(({ r, ep }) => {
      // 補的列：這一頁配不到結束時用未處理清單配好的那一次（第十輪 B10-03），不先下「沒有收到結束紀錄」
      const e = ep?.end || !r.presetEp ? ep : r.presetEp;
      return e ? { ...r, ep: e } : r;
    });
    // 分頁邊界（第九輪 B9-09）：還有下一頁時，只有結束、又在已載入最舊一列 15 分鐘內的先不畫——它的開始多半在下一頁，載入後合成一列
    return hasMore && sort === 'time' ? dropTailEndOnly(merged, rows.length ? rows[rows.length - 1].ts : 0) : merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedRows, hasMore, sort]);
  // 筆數、分級標籤、長條圖直接用平台的事件次數（platform 已把同一次的開始＋結束算一次）；有沒有下一頁只看 next_cursor
  // 警報洪水合併（第四輪 R4-P1-2、B4-08）：依時間排序時，同一台＋同一種、相鄰 2 分鐘內的警報合成一列「× N 次」，可一次處理
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const visRows = useMemo(() => {
    const groups = groupAlarms(
      shownRows,
      (r) => (sort === 'time' && r.cls === 'alarm' && r.doc.class !== 'bookmark' ? `${r.doc.home_id}/${r.doc.device_ref}/${r.doc.key}` : null),
      (r) => r.ts,
      // 還沒處理的（open / acknowledged；不知道狀態的當還沒處理）同一台＋同一種一直併（第五輪 B5-10）
      { open: (r) => isUnhandled(infoOf(r.doc.event_id)?.state) },
    );
    // 警報分頁（依時間）：還沒處理的安防組 → 狀態組還沒恢復 → 已恢復 → 處理中 → 已結案 → 未登記；前四組開最久的在前（SPEC #vms-web-alarm-tidy）
    if (tab === 'alarms' && sort === 'time') {
      const keyOf = (g: AlarmGroup<Row>) => {
        const r = g.head;
        const st = infoOf(r.doc.event_id)?.state;
        const agg = handling(st) === 'unhandled' && r.cls === 'alarm' ? openAgg(r.doc.home_id, r.doc.device_ref, r.doc.key) : undefined;
        const oldest = g.rest.length ? g.rest[g.rest.length - 1].ts : r.ts;
        return {
          rank:
            r.cls === 'alarm' && r.doc.class !== 'bookmark'
              ? tidyRank({ state: st, recovered: recoveredAt(r.doc.key, r.ep) !== null, listed: listed(r.doc.home_id, r.doc.device_ref), key: r.doc.key })
              : TIDY_RANK.closed,
          earliest: Math.min(agg?.earliest ?? oldest, oldest),
          ts: r.ts,
          ref: r.doc.device_ref,
          key: r.doc.key,
          id: r.doc.event_id,
        };
      };
      const ks = new Map(groups.map((g) => [g, keyOf(g)]));
      groups.sort((a, b) => compareTidy(ks.get(a)!, ks.get(b)!));
    }
    return groups.flatMap((g) => {
      const id = `${g.head.doc.home_id}/${g.head.doc.event_id}`;
      const head = { r: g.head, group: g.rest.length ? g : null, member: false, gid: id };
      return openGroups.has(id) ? [head, ...g.rest.map((r) => ({ r, group: null, member: true, gid: id }))] : [head];
    });
  }, [shownRows, sort, openGroups, infoOf, tab, openAgg, listed]);
  // 一列代表幾次：還沒處理的合併列用完整的未處理清單（跟總覽同一個來源，第六輪 B6-03），其他是這一列併了幾筆
  const timesOf = (r: Row, group: AlarmGroup<Row> | null) => {
    const agg = isUnhandled(infoOf(r.doc.event_id)?.state) ? openAgg(r.doc.home_id, r.doc.device_ref, r.doc.key) : undefined;
    const localN = group ? group.rest.length + 1 : 1;
    // 即時進來、未處理清單還沒重撈到的開始（或沒有 phase 的）先 +1（第十輪 B10-04：進行中時還是 × 126，結束後才變 × 127）
    const liveExtra = agg ? (group ? [r, ...group.rest] : [r]).filter((x) => x.live && !agg.ids.includes(x.doc.event_id) && phaseOf(x.doc) !== 'ended').length : 0;
    return { agg, localN, n: Math.max((agg?.n ?? 0) + liveExtra, localN) };
  };
  // 一列警報的次數、整波、「× N 次（最早 HH:MM）」、已開多久（清單與工作台共用）
  const rowFacts = (r: Row, group: AlarmGroup<Row> | null, member: boolean) => {
    // 還沒處理的合併列：次數、最早時間與〔處理 ▾〕的範圍都用「還沒處理的完整清單」（跟總覽同一個來源，第六輪 B6-03）
    const { agg, localN, n } = timesOf(r, group);
    const sinceTs = agg && agg.n >= localN ? agg.earliest : group ? group.rest[group.rest.length - 1].ts : r.ts;
    const also0 = agg && agg.n >= localN ? agg.ids.filter((x) => x !== r.doc.event_id) : group ? group.rest.map((x) => x.doc.event_id) : [];
    // 合成一列的開始＋結束（第八輪 R8-P1-1）：〔處理 ▾〕連結束那筆一起
    const epEnd = r.ep?.end && r.ep.end.id !== r.doc.event_id && isUnhandled(infoOf(r.ep.end.id)?.state) ? r.ep.end.id : undefined;
    const also = epEnd && !also0.includes(epEnd) ? [...also0, epEnd] : also0;
    // 最早那次跨天要寫日期（第七輪 B7-03：「最早 16:08」其實是 10-12）
    const since = sinceStamp(sinceTs, r.ts, tzOf(r.doc.home_id));
    // 完整的未處理清單還沒到（剛切範圍）：先寫「計算中」，不寫最近 200 則算出來的次數（第八輪 B8-03）
    const counting = !openReady && isUnhandled(infoOf(r.doc.event_id)?.state);
    const label = counting ? t('events.times_counting') : !since ? t('events.times_only', { n }) : t('events.times_n', { n, since });
    const st = infoOf(r.doc.event_id)?.state;
    // 已開多久（還沒結案的；合併列用最早那則，SPEC #vms-web-alarm-tidy）
    const age = handling(st) !== 'closed' && !member ? openAgeText(t, Math.min(sinceTs, r.ts), now) : '';
    return { n, also, epEnd, label, counting, age, sinceTs };
  };
  // 警報分頁每一列的次數加起來已經涵蓋平台的警報次數：讀更多只會併進既有列 → 不放〔載入更多〕，寫「已載入全部 N 次（合併成 M 列）」
  // （第十一輪 B11-03：lstw 3 列已是 492 次，按兩次畫面沒變）
  const heads = visRows.filter((v) => !v.member);
  const allLoaded =
    tab === 'alarms' && sort === 'time' && !q && range === 'any' && openReady && hasMore && total > 0 &&
    heads.reduce((n, v) => n + (v.r.cls === 'alarm' && v.r.doc.class !== 'bookmark' ? timesOf(v.r, v.group).n : 1), 0) >= total;
  const loadMore = (
    <>
      {query.hasNextPage && !allLoaded && (
        <button className="btn" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {query.isFetchingNextPage ? t('common.loading') : t('search.more')}
        </button>
      )}
      {allLoaded && (
        <p className="muted small" data-testid="alarms-all-loaded">
          {t('events.all_loaded', { n: total, rows: heads.length })}
        </p>
      )}
    </>
  );
  const counts = useMemo(() => {
    const c: Record<VmsEventClass, number> = { alarm: 0, detection: 0, motion: 0, sensor: 0, system: 0, other: 0 };
    for (const f of levelFacet) c[isLevel(f.value) ? f.value : 'other'] += f.count;
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(levelFacet)]);

  const seen = useEventSeen();
  const unseen = (r: Row) => !r.live && !!r.api && isUnseen(seen.byApi.get(r.api), r.doc.event_id, r.doc.occurred_at);
  // 分級標籤 = 單選篩選（P1-3 ①、B-15）：點一個 = 只看它（其他變灰），再點一次 = 回到全部；按住 ⌘ / Ctrl 才多選
  const filtering = !isDefaultLevels(enabled);
  const toggle = (c: VmsEventClass, multi: boolean) => setEnabled((s) => nextLevels(s, c, multi));
  const findDev = (ref: string): VmsDevice | undefined => {
    for (const h of visible) {
      const d = byAid.get(`${h.home_id}/${ref}`);
      if (d) return d;
    }
    return undefined;
  };
  const knownName = (ref: string): string | undefined => {
    const d = findDev(ref);
    return d ? fullName(d, tree) : undefined;
  };
  // 清單裡沒有的設備不顯示 device_ref（P0-7）：NVR 沒登記的通道寫「NVR631 / CH5（未登記的通道）」，其他寫「不在清單裡的設備」（總覽同一個函式，B7-02）
  const devName = (ref: string): string => eventDeviceLabel(t, findDev, tree, ref);
  // 警報工作台的列（EVT-07）：同一份排好、合併好的列，一列一則（合併列不展開，整波在右欄）
  const wbItems: WbItem[] = wbOn
    ? heads
        .filter((v) => v.r.cls === 'alarm' && v.r.doc.class !== 'bookmark')
        .map(({ r, group }) => {
          const f = rowFacts(r, group, false);
          return {
            id: `${r.doc.home_id}/${r.doc.event_id}`,
            doc: r.doc,
            device: r.device,
            homeName: r.homeName,
            ts: r.ts,
            ep: r.ep,
            name: eventName(t, r.doc.key, r.doc.class, r.doc.payload, r.doc.device_ref),
            devLabel: r.device ? fullName(r.device, tree) : devName(r.doc.device_ref),
            times: f.counting || f.n > 1 ? f.label : '',
            n: f.n,
            also: f.n > 1 ? f.also : f.epEnd ? [f.epEnd] : [],
            since: f.sinceTs,
            age: f.age,
            unseen: unseen(r),
            listed: listed(r.doc.home_id, r.doc.device_ref),
            startedBeforeAlarm: r.doc.phase === 'ended' && !r.ep?.start,
          };
        })
    : [];
  const facetKind = (ref: string): 'known' | 'channel' | 'unknown' => {
    if (knownName(ref)) return 'known';
    const ch = parseChildAid(ref);
    return ch && knownName(ch.parent) ? 'channel' : 'unknown';
  };
  const devFacet = groupDeviceFacet(deviceFacet, facetKind);
  const addToken = (tok: string) => setInput((v) => (v.trim() ? v.trim() + ' ' : '') + tok);

  // 輸入中的最後一個詞：device: / class: / tag: / node: / view: / group: 給提示
  const last = input.split(/\s+/).pop() ?? '';
  const lastKey = last.includes(':') ? last.slice(0, last.indexOf(':')).toLowerCase() : ''; // Node: / VIEW: 也認得
  const lastVal = last.includes(':') ? last.slice(last.indexOf(':') + 1) : '';
  // node:：伺服器搜樹（/v1/me/tree?q=，至少 2 字，同樹面板）；值是 site_id（經委派 site_id@delegation_id），提示顯示名稱 + 路徑
  const [nodeQ, setNodeQ] = useState('');
  useEffect(() => {
    const v = lastKey === 'node' && lastVal.trim().length >= 2 && !lastVal.includes(',') ? lastVal.trim() : '';
    const id = setTimeout(() => setNodeQ(v), 300);
    return () => clearTimeout(id);
  }, [lastKey, lastVal]);
  const nodeHits = useQuery({
    queryKey: ['me-tree', 'search-suggest', planes.map((p) => p.api).join(','), nodeQ],
    enabled: nodeQ !== '' && planes.length > 0,
    staleTime: 30_000,
    retry: false,
    // 各 plane 分開查：全部失敗 → 錯誤狀態（顯示「節點搜尋失敗」，不當成找不到）；部分失敗 → 有結果照列、另外標出來
    queryFn: async () => {
      const rs = await Promise.allSettled(planes.map((p) => meTreeApi.tree(p, { q: nodeQ, limit: 20 })));
      const ok = rs.filter((r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof meTreeApi.tree>>> => r.status === 'fulfilled');
      if (ok.length === 0) throw (rs[0] as PromiseRejectedResult).reason;
      return { items: ok.flatMap((r) => r.value?.items ?? []) as MeTreeItem[], partial: ok.length < rs.length };
    },
  });
  // view: / group:：我看得到的檢視與群組（個人 + 組織的），用名稱比對
  const wantVG = lastKey === 'view' || lastKey === 'group' || orgTreeTokens(q).some((x) => x.field !== 'node'); // 同一套解析（大小寫不敏感）
  const { views: viewsQ, groups: groupsQ } = useViewsAndGroups(wantVG ? planes : [], wantVG ? orgs.filter((o) => o.kind === 'team') : []);
  const suggestions: Suggestion[] = useMemo(() => {
    const [k0, v = ''] = last.split(':');
    const k = k0.toLowerCase();
    if (!last.includes(':')) return last.length >= 1 ? FIELDS.filter((f) => f.startsWith(last.toLowerCase()) && f !== last).map((f) => ({ label: f, insert: f })) : [];
    if (lastKey === 'node') {
      const seen = new Set<string>();
      return (nodeHits.data?.items ?? [])
        .filter((it) => it.kind === 'node')
        .map((it) => ({ label: [...(it.path ?? []).map((x) => x.name), it.name].join(' › '), insert: 'node:' + nodeRefOf(it) }))
        .filter((s) => (seen.has(s.insert) ? false : (seen.add(s.insert), true)))
        .slice(0, 8);
    }
    if (lastKey === 'view' || lastKey === 'group') {
      const pool = lastKey === 'view' ? (viewsQ.data ?? []).map((x) => ({ id: x.view_id, name: x.name })) : (groupsQ.data ?? []).map((x) => ({ id: x.group_id, name: x.name }));
      return pool
        .filter((x) => x.name.toLowerCase().includes(lastVal.toLowerCase()) && x.id !== lastVal)
        .slice(0, 8)
        .map((x) => ({ label: x.name, insert: `${lastKey}:${x.id}` }));
    }
    const pool =
      k === 'device'
        ? tree.flatMap((d) => [d, ...d.children]).map((d) => d.name)
        : k === 'site'
          ? sites.map((x) => x.name)
          : k === 'class'
            ? Object.keys(LEVEL_OF_CLASS)
            : k === 'level'
              ? CLASSES
              : k === 'tag'
                ? tagFacet.map((f) => f.value)
                : [];
    return [...new Set(pool)]
      .filter((x) => x && x.toLowerCase().includes(v.toLowerCase()) && x !== v)
      .slice(0, 8)
      .map((x) => {
        const tok = `${k}:${/\s/.test(x) ? `"${x}"` : x}`;
        return { label: tok, insert: tok };
      });
  }, [last, lastKey, lastVal, tree, tagFacet, sites, nodeHits.data, viewsQ.data, groupsQ.data]);
  // 篩選中的 node: / view: / group: 換成名稱（值是 id，人看不懂）。node 沒有「id → 名稱」的查詢，名稱記在選提示的時候，
  // 存在這個瀏覽器（套用已存搜尋、重新整理後還認得）；不認得的照列 id 並標「名稱未知」。
  const namesKey = nodeNamesKey(meId);
  const loadNames = (key: string): Record<string, string> => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, string>;
    } catch {
      return {};
    }
  };
  const [names, setNames] = useState<{ key: string; map: Record<string, string> }>(() => ({ key: namesKey, map: loadNames(namesKey) }));
  // 換帳號（同一個頁面 session 換人）：改讀那個人的
  const nodeNames = names.key === namesKey ? names.map : loadNames(namesKey);
  useEffect(() => {
    if (names.key !== namesKey) setNames({ key: namesKey, map: loadNames(namesKey) });
  }, [namesKey, names.key]);
  const setNodeNames = (f: (m: Record<string, string>) => Record<string, string>) => setNames((s) => ({ key: namesKey, map: f(s.key === namesKey ? s.map : loadNames(namesKey)) }));
  useEffect(() => {
    if (names.key !== namesKey || !meId) return;
    try {
      localStorage.removeItem('vms.search.nodeNames'); // 舊版沒分帳號的快取
      const keys = Object.keys(names.map);
      const keep = keys.length > 200 ? Object.fromEntries(keys.slice(-200).map((k) => [k, names.map[k]])) : names.map;
      localStorage.setItem(namesKey, JSON.stringify(keep));
    } catch {
      /* 私密視窗等存不了：只影響顯示名稱 */
    }
  }, [names, namesKey, meId]);
  const treeFilters = useMemo(
    () =>
      orgTreeTokens(q).map(({ field, value }) => {
        const name =
          field === 'node'
            ? nodeNames[value]
            : field === 'view'
              ? viewsQ.data?.find((x) => x.view_id === value)?.name
              : groupsQ.data?.find((x) => x.group_id === value)?.name;
        return { field, value, name };
      }),
    [q, nodeNames, viewsQ.data, groupsQ.data],
  );
  const applySuggestion = (s: Suggestion) => {
    if (s.insert.startsWith('node:') && s.label !== s.insert) setNodeNames((m) => ({ ...m, [s.insert.slice(5)]: s.label }));
    setInput((v) => v.slice(0, v.length - last.length) + s.insert + ' ');
  };

  // 鍵盤（SPEC #vms-web-973「鍵盤」、P2-3）：J / K 移到下一筆 / 上一筆、Enter 開那一筆的詳細
  const [kbd, setKbd] = useState(-1);
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (e) => {
    if (wbOn || ignoreHotkey(e)) return; // 警報工作台有自己的一組鍵（AlarmWorkbench）
    const rows = [...document.querySelectorAll<HTMLElement>('ul.event-list > li.event-row')];
    if (!rows.length) return;
    const k = e.key.toLowerCase();
    if (k === 'j' || k === 'k') {
      e.preventDefault();
      const n = Math.max(0, Math.min(rows.length - 1, kbd + (k === 'j' ? 1 : -1)));
      setKbd(n);
      rows[n].scrollIntoView?.({ block: 'nearest' });
    } else if (e.key === 'Enter' && kbd >= 0 && rows[kbd] && (document.activeElement === document.body || !document.activeElement)) {
      e.preventDefault();
      rows[kbd].querySelector<HTMLAnchorElement>('a[data-open]')?.click();
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  return (
    <section>
      <h1 className="page-title">{t('nav.events')}</h1>
      <div className="dtabs" role="tablist" aria-label={t('nav.events')} data-testid="events-tabs">
        {EVENTS_TABS.map((x) => (
          <button key={x} role="tab" aria-selected={tab === x} onClick={() => setTab(x)} data-tab={x}>
            {x === 'alarms'
              ? unhandled
                ? t(unhandledMore ? 'events.tab_alarms_more' : 'events.tab_alarms_n', { count: unhandled })
                : unhandled === null && hasUnhandled
                  ? t('events.tab_alarms_counting')
                  : t('events.tab_alarms')
              : t('events.tab_' + x)}
          </button>
        ))}
      </div>
      {tab === 'saved' ? (
        <SavedSearches
          inline
          query={input.trim()}
          filters={{ sort, levels: [...enabledAll] }}
          onApply={(sv) => {
            setInput(sv.query);
            setQ(sv.query);
            if (sv.filters.sort === 'time' || sv.filters.sort === 'relevance') setSort(sv.filters.sort);
            const lv = (sv.filters.levels ?? []).filter(isLevel);
            if (lv.length) setEnabled(new Set(lv));
            setTab('all');
          }}
        />
      ) : (
        <>
      <div className="search-box">
        <input
          className="input"
          type="search"
          placeholder={t('search.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setQ(input.trim())}
          aria-label={t('search.placeholder')}
        />
        <select className="input" value={shownRange} onChange={(e) => setRange(e.target.value as TimeRange)} aria-label={t('search.range')} data-testid="event-range">
          {TIME_RANGES.map((r) => (
            <option key={r} value={r}>
              {t('search.range_' + r)}
            </option>
          ))}
        </select>
        <select className="input" value={sort} onChange={(e) => setSort(e.target.value as 'time' | 'relevance')} aria-label={t('search.sort')}>
          <option value="time">{t('search.sort_time')}</option>
          <option value="relevance">{t('search.sort_relevance')}</option>
        </select>
      </div>
      {suggestions.length > 0 && (
        <div className="search-suggest" role="listbox" aria-label={t('search.suggest')}>
          {suggestions.map((s) => (
            <button key={s.insert} className="chip" role="option" aria-selected={false} onClick={() => applySuggestion(s)} title={s.insert}>
              {s.label !== s.insert && <span className="muted">{s.insert.slice(0, s.insert.indexOf(':') + 1)}</span>}
              {s.label}
            </button>
          ))}
        </div>
      )}
      {lastKey === 'node' && nodeQ !== '' && nodeHits.isSuccess && !nodeHits.data?.partial && suggestions.length === 0 && (
        <p className="muted small" data-testid="node-suggest-empty">
          {t('search.node_none', { q: nodeQ })}
        </p>
      )}
      {lastKey === 'node' && nodeQ !== '' && (nodeHits.isError || nodeHits.data?.partial) && (
        <p className="notice small" data-testid="node-suggest-error">
          {t(nodeHits.isError ? 'search.node_error' : 'search.node_partial')}
        </p>
      )}
      {treeFilters.length > 0 && (
        <p className="muted small" data-testid="tree-filters">
          {t('search.tree_filters', {
            list: treeFilters
              .map((f) => t(`search.tree_field_${f.field}`, { name: f.name ?? t('search.tree_name_unknown', { id: f.value }) }))
              .join(t('common.list_sep')),
          })}
        </p>
      )}
      {shownRange === 'custom' && (
        <div className="row" data-testid="event-range-custom">
          <DateTimeInput value={custom.from} onChange={(v) => setCustom({ ...custom, from: v })} aria-label={t('search.range_from')} />
          <span>–</span>
          <DateTimeInput value={custom.to} onChange={(v) => setCustom({ ...custom, to: v })} aria-label={t('search.range_to')} />
        </div>
      )}
      {!wbOn && (
      <details className="syntax-help small">
        <summary>{t('search.syntax_btn')}</summary>
        <p className="muted">{t('search.syntax')}</p>
        <p className="muted">{t('search.syntax_dates')}</p>
      </details>
      )}
      {warnings.length > 0 && <p className="notice small">{t('search.warnings', { list: warnings.join(t('common.list_sep')) })}</p>}
      {evTerms.hits.length > 0 && (
        <p className="muted small" data-testid="search-term-class">
          {t('search.term_as_class', { terms: evTerms.hits.map((h) => `「${h.term}」`).join(t('common.list_sep')), cls: t(`events.cls_name.${evTerms.hits[0].cls}`) })}
        </p>
      )}
      {siteSc.unknown.length > 0 && (
        <p className="notice small" data-testid="site-unknown">
          {t('search.site_unknown', { list: siteSc.unknown.join(t('common.list_sep')) })}
        </p>
      )}
      {!wbOn && (
      <div className="toolbar wrap">
        {tab === 'all' && CLASSES.map((c) => (
          <button
            key={c}
            className={`chip ${filtering && enabled.has(c) ? 'on' : ''} ${filtering && !enabled.has(c) ? 'dim' : ''}`}
            aria-pressed={filtering && enabled.has(c)}
            title={t('events.chip_tip')}
            onClick={(e) => toggle(c, e.metaKey || e.ctrlKey)}
          >
            <span aria-hidden>{CLASS_ICON[c]}</span> {t(`events.class_${c}`)} <span className="muted">{counts[c]}</span>
          </button>
        ))}
        {/* 檢索引擎與耗時只放在滑過提示（客服用），不上畫面（B-13） */}
        <span className="muted" title={engine ? `${engine} ${took} ms` : undefined}>
          {query.isFetching ? t('common.loading') : t('events.count', { n: capped ? `${total.toLocaleString()}+` : total })}
        </span>
        <span className="spacer" />
        {/* 「全部標為已看」收進〔⋯〕（P1-3 ⑥） */}
        <MoreMenu label={t('events.more')} items={[{ key: 'mark-all-seen', label: <span data-testid="mark-all-seen">{t('events.mark_all_seen')}</span>, onClick: () => void seen.markAll() }]} />
      </div>
      )}
      {!wbOn && hourFacet.length > 1 && <HourHistogram buckets={hourFacet} tz={tz} onRange={(r) => (setCustomOpen(true), setCustom(r))} />}
      {!wbOn && (deviceFacet.length > 0 || tagFacet.length > 0) && (
        <div className="facet-row">
          {devFacet.main.map((f) => (
            <button key={f.value} className="chip" onClick={() => addToken(deviceToken(f.value, knownName(f.value)))} data-facet-device={f.value}>
              {devName(f.value)} <span className="muted">{f.count}</span>
            </button>
          ))}
          {devFacet.channels.length > 0 && (
            <button className="chip" aria-expanded={chOpen} onClick={() => setChOpen((o) => !o)} data-testid="facet-other-channels">
              {t(devFacet.unknown ? 'events.other_devices' : 'events.other_channels', { n: devFacet.channels.length })} {chOpen ? '▾' : '▸'}
            </button>
          )}
          {chOpen &&
            devFacet.channels.map((f) => (
              <button key={f.value} className="chip" onClick={() => addToken(deviceToken(f.value, knownName(f.value)))} data-facet-device={f.value}>
                {f.tail ? t('events.device_unknown_tail', { tail: f.tail }) : devName(f.value)} <span className="muted">{f.count}</span>
              </button>
            ))}
          {tagFacet.map((f) => (
            <button key={'t-' + f.value} className="chip" onClick={() => addToken(`tag:${f.value}`)}>
              #{tagLabel(t, f.value)} <span className="muted">{f.count}</span>
            </button>
          ))}
        </div>
      )}
      {query.error ? <p className="error">{String((query.error as Error).message)}</p> : null}
      {!wbOn && shownRows.length > 0 && (
        <div className="toolbar">
          <span className="spacer" />
          <Link
            className="btn btn-sm"
            to="/map"
            data-testid="events-to-map"
            state={{
              pins: shownRows
                .filter((r) => r.doc.class !== 'bookmark')
                .slice(0, 200)
                .map((r) => ({ deviceKey: `${r.doc.home_id}/${r.doc.device_ref}`, ts: r.ts, eventId: r.doc.event_id, homeId: r.doc.home_id, type: r.doc.key, level: r.cls })),
            }}
          >
            {t('search.to_map')}
          </Link>
        </div>
      )}
      {wbOn ? (
        <AlarmWorkbench items={wbItems} mode={wbMode === 'split' ? 'split' : 'drawer'} tree={tree} tzOf={tzOf} footer={loadMore} />
      ) : (
      <>
      <ul className="event-list">
        {visRows.map(({ r, group, member, gid }, i) => (
          <li
            key={`${r.doc.home_id}/${r.doc.event_id}`}
            className={`event-row cls-${r.cls}${r.live ? ' live-new' : ''}${unseen(r) ? ' unseen' : ''}${i === kbd ? ' kbd-cursor' : ''}${member ? ' event-row-member' : ''}`}
            data-group-n={group ? group.rest.length + 1 : undefined}
            aria-current={i === kbd ? 'true' : undefined}
            data-event={r.doc.event_id}
            data-live={r.live ? '1' : undefined}
            data-unseen={unseen(r) ? '1' : undefined}
          >
            <span className="event-icon" aria-hidden>
              {r.doc.class === 'bookmark' ? '🔖' : eventIcon(r.cls, r.doc.class)}
            </span>
            {r.doc.has_clip && r.api && r.doc.class !== 'bookmark' && (
              <EventThumb api={r.api} homeId={r.doc.home_id} device={r.doc.device_ref} atMs={r.ts} alt={t('events.thumb_alt')} lens={lensOf(r.doc.payload)} />
            )}
            <div className="event-main">
              <div>
                <strong>{r.doc.class === 'bookmark' ? String(r.doc.payload?.title ?? '') : eventName(t, r.doc.key, r.doc.class, r.doc.payload, r.doc.device_ref)}</strong>
                <span className="muted"> · {r.device ? fullName(r.device, tree) : devName(r.doc.device_ref)}</span>
                <LensChip cam={r.device} lens={lensOf(r.doc.payload)} />
                {r.ep && <EpisodeNote ep={r.ep} eventKey={r.cls === 'alarm' ? r.doc.key : undefined} tz={tzOf(r.doc.home_id)} />}
                <CloudAiBadge payload={r.doc.payload} />
                <SourceBadge cls={r.doc.class} payload={r.doc.payload} />
                {r.doc.ai_tags
                  .filter((tag) => !tag.startsWith('cloud_ai_'))
                  .map((tag) => (
                    <span key={tag} className="badge" style={{ marginLeft: 6 }}>
                      #{tagLabel(t, tag)}
                    </span>
                  ))}
              </div>
              {typeof (r.doc.payload?.cloud_ai as { caption?: unknown } | undefined)?.caption === 'string' && (
                <div className="small" data-testid="cloud-ai-caption">
                  {String((r.doc.payload?.cloud_ai as { caption: string }).caption)}
                </div>
              )}
              <div className="muted small">
                {r.homeName} · {formatDateTime(r.ts / 1000, tzOf(r.doc.home_id), i18n.language)}
                {r.doc.payload?.producer ? ` · ${t('search.producer', { id: devName(String(r.doc.payload.producer)) })}` : ''}
              </div>
              {r.cls === 'alarm' && r.doc.class !== 'bookmark' && (
                <div className="event-alarm">
                  {(() => {
                    const { n, also, epEnd, label, counting, age } = rowFacts(r, group, member);
                    return (
                      <>
                        <AlarmActions
                          menu
                          homeId={r.doc.home_id}
                          eventId={r.doc.event_id}
                          alsoIds={n > 1 && !member && !openGroups.has(gid) ? also : epEnd ? [epEnd] : undefined}
                          recovered={!member && recoveredAt(r.doc.key, r.ep) !== null}
                        />
                        {age && (
                          <span className="muted small alarm-age" data-testid="alarm-age">
                            {age}
                          </span>
                        )}
                        {/* 展開後的子列不再帶合併膠囊（第七輪 B7-04：每一筆子列都重複「× 512 次」） */}
                        {(n > 1 || counting) && !group && !member && (
                          <span className="chip alarm-times" data-testid="alarm-group" data-counting={counting ? '1' : undefined}>
                            {label}
                          </span>
                        )}
                        {group && (
                          <button
                            type="button"
                            className="chip alarm-times"
                            aria-expanded={openGroups.has(gid)}
                            onClick={() => setOpenGroups((s) => (s.has(gid) ? new Set([...s].filter((x) => x !== gid)) : new Set(s).add(gid)))}
                            data-testid="alarm-group"
                            data-counting={counting ? '1' : undefined}
                          >
                            {label} {openGroups.has(gid) ? '▾' : '▸'}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
            {r.live && <span className="badge badge-ok">{t('events.live_new')}</span>}
            {/* 分類只是標示，不做成像按鈕的膠囊（第四輪 R4-P1-2） */}
            <span className="muted small event-cls">{t(`events.class_${r.cls}`)}</span>
            {r.doc.class !== 'bookmark' && (
              <Link
                className="btn btn-sm"
                data-open
                to={`/events/${encodeURIComponent(r.doc.home_id)}/${encodeURIComponent(r.doc.event_id)}`}
                state={{ doc: r.doc }}
                onClick={() => r.api && void seen.mark(r.api, [r.doc.event_id])}
              >
                {t('evd.open')}
              </Link>
            )}
            {r.device?.isCamera && r.device.presence === 'online' && (
              <Link className="btn btn-sm" to="/live" state={{ focus: r.device.key }}>
                {t('devices.watch')}
              </Link>
            )}
            {/* 回放：雲端、NVR、SD 卡都算（B-18：只認 NVR 通道，SD 卡的攝影機沒有回放鈕）；回放頁自己挑有錄影的來源 */}
            {r.device?.isCamera && can(r.device, 'recording.read') && (
              <Link className="btn btn-sm" to={playbackHref(r.device.key, r.ts / 1000, { event: r.doc.event_id, lens: lensOf(r.doc.payload) })}>
                {t('live.watch_playback')}
              </Link>
            )}
          </li>
        ))}
        {!query.isLoading && visRows.length === 0 && <li className="muted">{t('events.empty')}</li>}
      </ul>
      {loadMore}
      </>
      )}
        </>
      )}
    </section>
  );
}

/** 分頁邊界的「只有結束」列（第九輪 B9-09）：在 oldestTs 之後 15 分鐘內的先拿掉（開始多半在下一頁）。 */
export const TAIL_END_ONLY_MS = 15 * 60_000;
export function dropTailEndOnly<R extends { ts: number; ep?: Episode }>(rows: R[], oldestTs: number): R[] {
  if (!oldestTs) return rows;
  return rows.filter((r) => !(r.ep && !r.ep.start && r.ep.end && r.ts - oldestTs <= TAIL_END_ONLY_MS));
}

/** 同一次的開始＋結束合成一列的說明：持續 N 秒 / 進行中 / 沒有收到結束紀錄 / 結束（第八輪 R8-P1-1）。攝影機事件分頁共用。 */
export function EpisodeNote({ ep, eventKey, tz }: { ep: Episode; eventKey?: string; tz?: string }) {
  const { t } = useTranslation();
  const st = episodeState(ep);
  // 狀態類警報（影像訊號中斷）收到結束紀錄 =「↺ 已恢復 HH:MM（中斷了 N）」（SPEC #vms-web-alarm-tidy）；灰綠字、不畫框
  const rec = recoveredAt(eventKey, ep);
  if (rec !== null && st.kind === 'done')
    return (
      <span className="alarm-recovered small" style={{ marginLeft: 6 }} data-testid="alarm-recovered" title={stamp(rec, { tz })}>
        {st.sec < 1 ? t('alarm.recovered_lt1', { at: nearStamp(rec, Date.now(), tz) }) : t('alarm.recovered_dur', { at: nearStamp(rec, Date.now(), tz), dur: durationText(t, st.sec) })}
      </span>
    );
  if (st.kind === 'done')
    return (
      // 時長不拆行（第十輪 B10-06：570 寬「持續 36 秒」在「36」與「秒」之間斷行）
      <span className="muted small" style={{ marginLeft: 6, whiteSpace: 'nowrap' }} data-testid="event-episode" data-state="done">
        {st.sec < 1 ? t('events.ep_lasted_lt1') : t('events.ep_lasted', { dur: durationText(t, st.sec) })}
      </span>
    );
  return (
    <span className={'badge' + (st.kind === 'open' ? ' badge-ok' : '')} style={{ marginLeft: 6, whiteSpace: 'nowrap' }} data-testid="event-episode" data-state={st.kind}>
      {t('events.ep_' + st.kind)}
    </span>
  );
}

/** 雲端 AI 的判定（vms-web §5.10 AI-02）：原事件 payload.cloud_ai = {verdict, labels, caption}。 */
/**
 * AIBOX-05：偵測事件依分析來源並列——AI box / NPU（edge_box）標出來；雲端 AI 的判定由 CloudAiBadge 顯示；
 * 沒標來源的偵測 = 設備端 AI。其他類別的事件不標。
 */
export function analyticsSource(cls: string, payload?: Record<string, unknown>): 'edge_box' | 'cloud' | 'device' | null {
  const src = payload?.analytics_source;
  if (src === 'edge_box' || src === 'cloud') return src;
  return ['person', 'human', 'vehicle', 'pet', 'package'].includes(cls) ? 'device' : null;
}

function SourceBadge({ cls, payload }: { cls: string; payload?: Record<string, unknown> }) {
  const { t } = useTranslation();
  const src = analyticsSource(cls, payload);
  if (!src || src === 'cloud') return null; // 雲端的由 CloudAiBadge 顯示
  return (
    <span className="badge" style={{ marginLeft: 6 }} data-testid="source-badge" data-source={src}>
      {t(`search.src_${src}`)}
    </span>
  );
}

function CloudAiBadge({ payload }: { payload?: Record<string, unknown> }) {
  const { t } = useTranslation();
  const ai = payload?.cloud_ai as { verdict?: string; labels?: string[] } | undefined;
  if (!ai?.verdict) return null;
  if (ai.verdict === 'no_target')
    return (
      <span className="badge" style={{ marginLeft: 6 }} data-testid="cloud-ai-badge">
        {t('aiCloud.badge_no_target')}
      </span>
    );
  const main = (ai.labels ?? []).filter((l) => ['person', 'vehicle', 'animal', 'package'].includes(l));
  return (
    <span className="badge badge-ok" style={{ marginLeft: 6 }} data-testid="cloud-ai-badge">
      {t('aiCloud.badge_confirmed', { what: main.map((l) => t(`aiCloud.label_${l}`)).join('、') })}
    </span>
  );
}

/**
 * facet 點設備 → 搜尋框插入的語法：有名稱就用名稱（`device:"NVR631 / CH5"`，平台照顯示名稱比對，VMS UX 評審 2026-10-13 P0-7、B-16），
 * 名稱裡有空白、斜線或引號以外的特殊字就加引號；查不到名稱才用 device_ref。
 */
export function deviceToken(ref: string, name: string | undefined): string {
  const n = (name ?? '').replace(/"/g, '').trim();
  if (!n || n === ref) return `device:${ref}`;
  return /[\s/:,]/.test(n) ? `device:"${n}"` : `device:${n}`;
}

/** 預設的分級（系統事件預設不列）。 */
export const DEFAULT_LEVELS: VmsEventClass[] = ['alarm', 'detection', 'motion', 'sensor', 'other'];
export function isDefaultLevels(s: Set<VmsEventClass>): boolean {
  return s.size === DEFAULT_LEVELS.length && DEFAULT_LEVELS.every((c) => s.has(c));
}

/** 點分級標籤後的集合：單選（再點一次回到全部）；multi（⌘ / Ctrl）= 在篩選中加減一個，減到空就回到全部。 */
export function nextLevels(s: Set<VmsEventClass>, c: VmsEventClass, multi: boolean): Set<VmsEventClass> {
  const def = new Set(DEFAULT_LEVELS);
  if (!multi) return !isDefaultLevels(s) && s.size === 1 && s.has(c) ? def : new Set([c]);
  if (isDefaultLevels(s)) return new Set([c]);
  const n = new Set(s);
  if (n.has(c)) n.delete(c);
  else n.add(c);
  return n.size ? n : def;
}

export type TimeRange = 'any' | 'today' | 'yesterday' | '24h' | '7d' | 'custom';
export const TIME_RANGES: TimeRange[] = ['any', 'today', 'yesterday', '24h', '7d', 'custom'];

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * 時間範圍 → 搜尋語法（平台 search.Parse，SPEC #vms-web-496 日期語意）：今天 / 昨天 = on:<那一天>（使用者時區的日界，請求帶 tz）；
 * 過去 24 小時 = after:<RFC 3339>；過去 7 天 = after:<6 天前>（含當天，共 7 個日曆天）；自訂 = after: / before:（到分鐘，datetime-local 是本機時間）。
 */
export function rangeTokens(r: TimeRange, now: Date, custom: { from: string; to: string } = { from: '', to: '' }): string {
  switch (r) {
    case 'today':
      return 'on:' + ymd(now);
    case 'yesterday':
      return 'on:' + ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
    case '24h':
      return 'after:' + new Date(now.getTime() - 86400_000).toISOString().replace(/\.\d{3}Z$/, 'Z');
    case '7d':
      return 'after:' + ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
    case 'custom': {
      const iso = (v: string) => new Date(v).toISOString().replace(/\.\d{3}Z$/, 'Z');
      return [custom.from && `after:${iso(custom.from)}`, custom.to && `before:${iso(custom.to)}`].filter(Boolean).join(' ');
    }
    default:
      return '';
  }
}
